/**
 * TODO: after migrate all project to Tailwind,
 * reenable these rules and fix accessibility issues
 */

/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import Icon from '../Icon';
import Text from '../Text';

const BORDER_SIZE = 2;
const TIME_TO_WAIT_BEFORE_FONT_IS_LOADED_IN_MILLISECONDS = 800;

const getMaxHeightCssValue = ({
  isOpen,
  isLoading,
  headerHeight,
  childHeight,
}) => {
  if (isOpen) {
    return `calc(${childHeight + BORDER_SIZE}px + ${headerHeight}px)`;
  }

  if (!isLoading && !isOpen) {
    return `${headerHeight}px`;
  }

  return 'unset';
};

const omitAlreadyAssignedProps = (props) => {
  const newProps = { ...props };

  if (newProps?.onClick) {
    delete newProps.onClick;
  }

  if (newProps?.className) {
    delete newProps.className;
  }

  return newProps;
};

function Collapse({
  children,
  className,
  full = false,
  headerProps,
  open = false,
  title,
  ...rest
}) {
  const collapseBodyRef = useRef(null);
  const collapseHeaderRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    let timeout = null;

    // TODO: Use CSS Font Loading API instead
    // See: https://developer.mozilla.org/en-US/docs/Web/API/CSS_Font_Loading_API
    timeout = setTimeout(() => {
      setHeaderHeight(collapseHeaderRef.current.clientHeight);
    }, TIME_TO_WAIT_BEFORE_FONT_IS_LOADED_IN_MILLISECONDS);

    return () => {
      clearTimeout(timeout);
    };
  }, [collapseHeaderRef.current]);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  const onClickHandler = (event) => {
    if (headerProps?.onClick) {
      headerProps.onClick(event, isOpen);
    }

    setIsOpen((prev) => !prev);
  };

  const isLoading = headerHeight === 0;

  return (
    <div
      aria-expanded={isOpen}
      className={twMerge(
        'relative flex max-h-[var(--max-height)] w-fit flex-col justify-between overflow-hidden rounded-2',
        full && 'w-full',
        !isLoading && 'transition-all duration-300 ease-in',
        className
      )}
      style={{
        '--max-height': getMaxHeightCssValue({
          isOpen,
          isLoading,
          headerHeight,
          childHeight: collapseBodyRef.current?.offsetHeight,
        }),
      }}
      {...rest}
    >
      {title && (
        <div
          ref={collapseHeaderRef}
          className={twMerge(
            'z-1 inline-flex cursor-pointer select-none items-start bg-neutral-high-200 px-4 py-2 text-neutral-low-400 dark:bg-neutral-low-400 dark:text-neutral-high-200',
            headerProps?.className
          )}
          onClick={onClickHandler}
          {...omitAlreadyAssignedProps(headerProps)}
        >
          <Text className="flex-1" bold>
            {title}
          </Text>
          <Icon
            className={twJoin(
              'ml-4 text-neutral-low-400 transition-all duration-300 ease-in dark:text-neutral-high-200',
              isOpen && 'rotate-180'
            )}
            name="arrow-down"
            size="size1"
          />
        </div>
      )}
      {!isLoading && (
        <div
          ref={collapseBodyRef}
          className="rounded-1 border-1 border-neutral-high-200 bg-neutral-high-100 px-4 pb-5 pt-4 text-neutral-low-300 dark:border-neutral-low-400 dark:bg-neutral-low-400 dark:text-neutral-high-300"
        >
          {children}
        </div>
      )}
    </div>
  );
}

Collapse.propTypes = {
  /**
   * Children to be rendered inside component
   */
  children: PropTypes.node.isRequired,
  /**
   * Collapse fills its parent width
   */
  full: PropTypes.bool,
  /**
   * Define if the collapse will be open or closed
   */
  open: PropTypes.bool,
  /**
   * Title to be rendered inside component
   */
  title: PropTypes.string.isRequired,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Customize `Collapse` header
   */
  headerProps: PropTypes.object,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback function on click
   */
  onClick: PropTypes.func,
};

export default Collapse;
